<template>
  <div class="space-y-14">
    <div class="body-1-bold">
      {{ i18n(translations.selectCategoryTitle) }}
    </div>

    <RevInputSelect
      :id="`category-${props.targetMarket}`"
      v-model="category"
      class="max-w-[40rem]"
      :label="i18n(translations.selectCategoryLabel)"
      :options="formattedCategories"
    />

    <div class="body-1-bold">
      {{ i18n(translations.ruleTitle) }}
    </div>

    <div class="flex items-center space-x-20">
      <span>
        {{ i18n(translations.rulePrice, { country: targetMarket }) }}
      </span>

      <span>&equals;</span>

      <span>
        {{
          i18n(translations.rulePrice, { country: sellerStore.referenceMarket })
        }}
      </span>

      <span>&times;</span>

      <RevInputText
        v-if="!hasFixedMultiplier"
        :id="`multiplier-${targetMarket}`"
        v-model.number="multiplier"
        :label="i18n(translations.ruleMultiplier)"
        :min="MIN_MULTIPLIER"
        :step="STEP_MULTIPLIER"
        type="number"
      />

      <RateExchangeTag
        v-if="hasFixedMultiplier"
        class="flex"
        :fixed-multiplier="fixedMultiplier!.toString()"
      />

      <span>&plus;</span>

      <RevInputNumber
        :id="`offset-${targetMarket}`"
        v-model.number="offset"
        :label="i18n(translations.ruleOffset)"
        type="number"
      />
    </div>

    <div v-if="hasMultiplier" class="text-static-info-hi">
      {{
        i18n(translations.ruleCalculation, {
          reference_country: i18n.country(sellerStore.referenceMarket || ''),
          reference_price: i18n.price(REFERENCE_PRICE),
          target_country: i18n.country(targetMarket),
          target_price: i18n.price(targetPrice.amount),
        })
      }}
    </div>

    <div
      v-if="!hasFixedMultiplier && hasPriceImpact"
      class="text-static-danger-hi body-1-bold"
    >
      {{ i18n(translations.warningPriceMessage) }}
    </div>

    <div class="text-static-info-hi body-1">
      {{
        i18n(translations.ruleHelpText, {
          reference_country: i18n.country(sellerStore.referenceMarket || ''),
          target_country: i18n.country(targetMarket),
        })
      }}
    </div>

    <div class="space-x-14">
      <RevButton
        :data-qa="`save-${targetMarket}`"
        :disabled="!hasMultiplier"
        variant="secondary"
        @click="handleSubmitRule"
      >
        {{
          isInEditMode
            ? i18n(translations.editRule)
            : i18n(translations.addRule)
        }}
      </RevButton>

      <RevButton
        v-if="hasRules"
        variant="secondaryDestructive"
        @click="emits('cancel')"
      >
        {{ i18n(translations.cancel) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { type ComputedRef, computed, ref } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models'
import type { CategoryLeafLight } from '@backmarket/http-api/src/api-specs-catalog/catalog-access'
import {
  type PricingRule,
  deletePricingRule,
  postPricingRule,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevInputNumber } from '@ds/components/InputNumber'
import { type Option, RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import {
  ALL_CATEGORIES,
  LOWER_BOUND_MULTIPLIER,
  MIN_MULTIPLIER,
  REFERENCE_PRICE,
  STEP_MULTIPLIER,
  UPPER_BOUND_MULTIPLIER,
} from './PricingRuleForm.constants'
import translations from './PricingRuleForm.translations'
import RateExchangeTag from './components/RateExchangeTag.vue'

const i18n = useI18n()
const sellerStore = useSellerInfoStore()
const { trackClick } = useTracking()
const { openErrorToast } = useTheToast()

const props = defineProps<{
  categories: CategoryLeafLight[]
  fixedMultiplier: number | undefined
  hasRules: boolean
  ruleToEdit?: PricingRule | null
  // targetMarket as string because test fails if put as MarketCountryCode
  targetMarket: string
}>()

const emits = defineEmits(['cancel', 'submit'])

const category = ref(
  props.ruleToEdit?.category?.id.toString() || ALL_CATEGORIES,
)
const multiplier = ref(props.ruleToEdit?.multiplier || '')
const offset = ref(Number(props.ruleToEdit?.offset.amount) || 0)
const { allMarkets } = useMarketplace()

const isInEditMode = computed(() => Boolean(props.ruleToEdit))

const formattedCategories: ComputedRef<Option[]> = computed(() => {
  return [
    {
      value: ALL_CATEGORIES,
      label: i18n(translations.allCategories),
    },
    ...props.categories.map(
      ({ marketplaceCategoryId, categoryName }): Option => ({
        value: String(marketplaceCategoryId),
        label: categoryName,
      }),
    ),
  ]
})

const hasFixedMultiplier = computed(() => {
  return props.fixedMultiplier && props.fixedMultiplier > 0
})

const hasMultiplier = computed(() => {
  if (hasFixedMultiplier.value) return true

  return Number(multiplier.value) > 0
})

const hasPriceImpact = computed(() => {
  const multiplierNumber = Number(multiplier.value)

  return (
    multiplierNumber > 0 &&
    (multiplierNumber <= LOWER_BOUND_MULTIPLIER ||
      multiplierNumber >= UPPER_BOUND_MULTIPLIER)
  )
})

const targetPrice = computed(() => {
  const currentMarket = allMarkets[props.targetMarket as MarketCountryCode]
  const multiplierValue = hasFixedMultiplier.value
    ? Number(props.fixedMultiplier)
    : Number(multiplier.value)

  return {
    amount: REFERENCE_PRICE * multiplierValue + Number(offset.value),
    currency: currentMarket.defaultCurrency,
  }
})

async function handleSubmitRule() {
  try {
    const ruleToSubmit = {
      ...insertIf(isInEditMode.value, { ...props.ruleToEdit }),
      category:
        category.value === ALL_CATEGORIES ? null : parseInt(category.value, 10),
      multiplier: Number(
        hasFixedMultiplier.value ? props.fixedMultiplier : multiplier.value,
      ),
      offset: offset.value,
      targetMarket: props.targetMarket,
    }

    trackClick({
      zone: 'pricing_tool',
      name: isInEditMode.value ? 'save_change_rule' : 'save_update_price',
      value: {
        sourceMarket: sellerStore.referenceMarket,
        targetMarket: props.targetMarket,
        categoryId: ruleToSubmit.category,
      },
    })

    // Very old code that said:
    // The edition of a category requires to delete the rule and create a brand new one
    // @see https://www.notion.so/Discussion-Pricing-rule-Scope-and-ID-change-bdbf90c15b6747fb93591b286ab858fa
    if (isInEditMode.value) {
      await $httpFetch(deletePricingRule, {
        pathParams: {
          ruleId: props.ruleToEdit?.id,
        },
      })
    }

    await $httpFetch(postPricingRule, { body: ruleToSubmit })

    emits('submit')
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  }
}
</script>
